import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ShoppingCart, User, Search, LogOut, Heart, Package, LayoutDashboard, ChevronDown, Sparkles, Map, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { AdvancedSearchDialog } from "@/components/search/AdvancedSearchDialog";
import { CurrencySelector } from "@/components/CurrencySelector";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { cn } from "@/lib/utils";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, isAdmin, signOut } = useAuth();
  const { totalItems, setIsCartOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "Marketplace", href: "/shop" },
    { name: "Gifts", href: "/gifts" },
    { name: "Our Story", href: "/story" },
  ];

  const exploreLinks = [
    { name: "Traditions", href: "/crafts", icon: Sparkles, desc: "Living craft traditions of India" },
    { name: "Heritage Map", href: "/regions", icon: Map, desc: "Explore crafts by region" },
    { name: "Artisans", href: "/artisans", icon: Users, desc: "Meet the hands that create" },
  ];

  return (
    <header 
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        scrolled 
          ? "bg-background/95 backdrop-blur-lg py-1 shadow-none" 
          : "bg-background/60 backdrop-blur-md py-2"
      )}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-12 md:h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group shrink-0">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary flex items-center justify-center transition-transform group-hover:scale-105">
              <span className="text-primary-foreground font-display text-lg md:text-xl font-bold">G</span>
            </div>
            <span className="font-display text-xl md:text-2xl font-semibold text-foreground tracking-tight">
              Gaon<span className="text-terracotta">ova</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-center flex-1 mx-8">
            <NavigationMenu>
              <NavigationMenuList className="gap-2">
                {navLinks.map((link) => (
                  <NavigationMenuItem key={link.name}>
                    <Link to={link.href}>
                      <NavigationMenuLink className={cn(
                        navigationMenuTriggerStyle(),
                        "bg-transparent hover:bg-transparent hover:text-terracotta transition-all duration-300 font-medium text-[15px] md:text-base relative group/link px-2",
                        location.pathname === link.href ? "text-primary font-semibold" : "text-foreground/80"
                      )}>
                        {link.name}
                        <span className={cn(
                          "absolute -bottom-1 left-2 right-2 h-0.5 bg-terracotta transition-all duration-300 origin-left scale-x-0 group-hover/link:scale-x-100",
                          location.pathname === link.href && "scale-x-100"
                        )} />
                      </NavigationMenuLink>
                    </Link>
                  </NavigationMenuItem>
                ))}

                <NavigationMenuItem>
                  <NavigationMenuTrigger className="bg-transparent hover:bg-transparent hover:text-terracotta font-medium text-[15px] md:text-base px-2 data-[state=open]:text-terracotta transition-colors">
                    Explore
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px] animate-in fade-in zoom-in-95 duration-200">
                      {exploreLinks.map((link) => (
                        <li key={link.name}>
                          <NavigationMenuLink asChild>
                            <Link
                              to={link.href}
                              className="block select-none space-y-1 rounded-xl p-3 leading-none no-underline outline-none transition-all hover:bg-terracotta/5 group"
                            >
                              <div className="flex items-center gap-2 text-sm font-semibold leading-none text-primary group-hover:text-terracotta transition-colors">
                                <link.icon className="h-4 w-4" />
                                {link.name}
                              </div>
                              <p className="line-clamp-2 text-sm leading-snug text-muted-foreground mt-1 group-hover:text-foreground/70">
                                {link.desc}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                      <li className="md:col-span-2 mt-2 pt-2 border-t border-border">
                         <Link to="/contact" className="flex items-center justify-between px-3 py-2 text-sm font-medium hover:text-terracotta transition-all group">
                            <span className="group-hover:translate-x-1 transition-transform">Have questions? Contact us</span>
                            <ChevronDown className="h-4 w-4 -rotate-90 group-hover:translate-x-1 transition-transform" />
                         </Link>
                      </li>
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-3">
            {/* Elegant Search Trigger */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="hidden xl:flex items-center gap-2 text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded-full px-4 h-10 border border-transparent hover:border-border/50 transition-all w-48 justify-start group"
              onClick={() => setIsSearchOpen(true)}
            >
              <Search className="h-4 w-4 text-terracotta group-hover:scale-110 transition-transform" />
              <span className="text-xs">Search crafts...</span>
            </Button>

            <Button variant="ghost" size="icon" aria-label="Search" className="xl:hidden hover:text-terracotta transition-colors" onClick={() => setIsSearchOpen(true)}>
              <Search className="h-5 w-5" />
            </Button>

            <div className="h-6 w-px bg-border mx-1 hidden lg:block" />

            <CurrencySelector compact />
            
            <Button variant="ghost" size="icon" aria-label="Cart" className="relative group hover:bg-transparent" onClick={() => setIsCartOpen(true)}>
              <ShoppingCart className="h-5 w-5 group-hover:text-terracotta group-hover:scale-110 transition-all" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-terracotta text-cream text-[10px] rounded-full flex items-center justify-center font-bold animate-in zoom-in shadow-sm">
                  {totalItems > 99 ? "99+" : totalItems}
                </span>
              )}
            </Button>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full hover:bg-muted/50 transition-colors">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 p-2 rounded-2xl shadow-xl border-border/50 animate-in fade-in slide-in-from-top-2">
                  <div className="px-3 py-2 text-[10px] font-bold text-muted-foreground uppercase tracking-[0.2em]">Account</div>
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center w-full cursor-pointer py-2.5 px-3 rounded-lg hover:bg-terracotta/5 group">
                      <User className="h-4 w-4 mr-3 text-terracotta/70 group-hover:text-terracotta transition-colors" />
                      <span className="font-medium">My Profile</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="flex items-center w-full cursor-pointer py-2.5 px-3 rounded-lg hover:bg-terracotta/5 group">
                      <Package className="h-4 w-4 mr-3 text-terracotta/70 group-hover:text-terracotta transition-colors" />
                      <span className="font-medium">My Orders</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/wishlist" className="flex items-center w-full cursor-pointer py-2.5 px-3 rounded-lg hover:bg-terracotta/5 group">
                      <Heart className="h-4 w-4 mr-3 text-terracotta/70 group-hover:text-terracotta transition-colors" />
                      <span className="font-medium">My Wishlist</span>
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator className="my-2" />
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center w-full cursor-pointer py-2.5 px-3 rounded-lg hover:bg-terracotta/10 text-terracotta">
                          <LayoutDashboard className="h-4 w-4 mr-3" />
                          <span className="font-semibold">Admin Dashboard</span>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator className="my-2" />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive cursor-pointer py-2.5 px-3 rounded-lg hover:bg-destructive/10">
                    <LogOut className="h-4 w-4 mr-3" />
                    <span className="font-medium">Sign Out</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                variant="default" 
                size="sm" 
                className="rounded-full px-6 font-semibold ml-2 shadow-md hover:shadow-terracotta/20 hover:scale-105 active:scale-95 transition-all duration-300 bg-primary text-primary-foreground" 
                onClick={() => navigate("/auth")}
              >
                Sign In
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-2 md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(true)}>
              <Search className="h-5 w-5" />
            </Button>
            <Button variant="ghost" size="icon" className="relative" onClick={() => setIsCartOpen(true)}>
              <ShoppingCart className="h-5 w-5" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-terracotta text-cream text-[10px] rounded-full flex items-center justify-center font-bold">
                  {totalItems}
                </span>
              )}
            </Button>
            <button
              className="p-2 hover:bg-muted rounded-full transition-colors"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-6 animate-in slide-in-from-top duration-300">
            <nav className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.href}
                    className="px-4 py-3 text-lg font-medium hover:text-primary hover:bg-muted/50 rounded-xl transition-all"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
              
              <div className="px-4 py-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-3 px-1">Explore</p>
                <div className="grid grid-cols-2 gap-2">
                  {exploreLinks.map((link) => (
                    <Link
                      key={link.name}
                      to={link.href}
                      className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl text-sm font-medium hover:bg-muted transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <link.icon className="h-4 w-4 text-terracotta" />
                      {link.name}
                    </Link>
                  ))}
                  <Link
                    to="/contact"
                    className="flex items-center gap-2 p-3 bg-muted/30 rounded-xl text-sm font-medium hover:bg-muted transition-colors col-span-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Contact Us
                  </Link>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3 px-4 pt-4 border-t border-border mt-2">
                <CurrencySelector />
                {user ? (
                  <Button variant="outline" size="sm" className="rounded-full gap-2 px-6" onClick={() => { navigate("/profile"); setIsMenuOpen(false); }}>
                    <User className="h-4 w-4" />
                    My Account
                  </Button>
                ) : (
                  <Button variant="default" size="sm" className="flex-1 rounded-full px-6" onClick={() => { navigate("/auth"); setIsMenuOpen(false); }}>
                    Sign In
                  </Button>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>

      <AdvancedSearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} />
    </header>
  );
};

export default Header;
